import {
    Button,
    type ButtonHierarchy,
    type ButtonSize,
    type ButtonState,
} from '@trustoo/components/Button/v2'
import React from 'react'
import { CodeSnippet, InlineCode, Section } from './shared'

// ---------- Button (v2) showcase ----------
// The first design-system component generated from Figma by the component-sync
// pipeline. It composes the tokens into a typed, token-only Tailwind component,
// shown here in every hierarchy/state/size so engineers can see the API and
// copy a usage snippet.

const BUTTON_HIERARCHIES: ButtonHierarchy[] = [
    'primary',
    'secondary',
    'tertiary',
    'link-color',
    'link-gray',
]
// Figma's "Buttons / Button destructive" set covers Primary, Secondary,
// Tertiary and a single Link (no separate link-gray), so we surface link-color.
const DESTRUCTIVE_HIERARCHIES: ButtonHierarchy[] = [
    'primary',
    'secondary',
    'tertiary',
    'link-color',
]
const BUTTON_SIZES: ButtonSize[] = ['sm', 'md', 'lg', 'xl']
const BUTTON_STATES: ButtonState[] = ['default', 'hover', 'selected', 'disabled', 'loading']

const hierarchyLabel = (hierarchy: ButtonHierarchy): string =>
    hierarchy.charAt(0).toUpperCase() + hierarchy.slice(1).replace('-', ' ')

// Inline glyph so the demos don't depend on a specific icon-set name.
const DemoIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
)

const BUTTON_USAGE = `import { Button } from '@trustoo/components/Button/v2'

// Default: primary, medium
<Button onClick={onSave}>Save</Button>

// Hierarchy + size
<Button hierarchy="secondary" size="lg">Cancel</Button>

// With icons
<Button iconLeading={<PlusIcon />}>Add item</Button>
<Button iconTrailing={<ChevronIcon />}>Next</Button>

// Icon-only (aria-label REQUIRED)
<Button iconOnly aria-label="Add item" iconLeading={<PlusIcon />} />

// Loading: keeps the label, swaps the leading icon for a spinner, sets aria-busy
<Button state="loading" iconLeading={<PlusIcon />}>Saving…</Button>

// Inline link styles
<Button hierarchy="link-color" underlined>Learn more</Button>

// Destructive: error palette, works with any hierarchy
<Button destructive onClick={onDelete}>Delete</Button>
<Button destructive hierarchy="secondary">Remove</Button>`

const STATE_LABEL: Record<ButtonState, string> = {
    default: 'Default',
    hover: 'Hover',
    selected: 'Selected',
    disabled: 'Disabled',
    loading: 'Loading',
}

// One hierarchy block mirrors a column-group of the Figma "Buttons / Button"
// sheet: size columns (a text group with leading + trailing icons, plus an
// icon-only group) crossed with the state rows. Figma also draws Hover and
// Focused rows, but those are native :hover / :focus-visible pseudo-classes on
// the real <button>, so they only show on interaction. The static matrix
// renders the three prop-driven states.
const ButtonMatrixBlock: React.FC<{ hierarchy: ButtonHierarchy; destructive?: boolean }> = ({
    hierarchy,
    destructive = false,
}) => {
    const isLink = hierarchy === 'link-color' || hierarchy === 'link-gray'
    return (
        <div className="rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
            <div className="mb-spacing-lg flex items-baseline gap-spacing-md">
                <span className="text-sm font-semibold text-text-primary">
                    {hierarchyLabel(hierarchy)}
                </span>
                <InlineCode>{hierarchy}</InlineCode>
            </div>
            <div className="overflow-x-auto">
                <div className="inline-block min-w-full align-top">
                    <div className="flex items-center gap-spacing-lg pb-spacing-md text-xs font-semibold uppercase text-text-quaternary">
                        <div className="w-16 shrink-0" />
                        {BUTTON_SIZES.map((size) => (
                            <div key={size} className="w-44 shrink-0">
                                {size}
                            </div>
                        ))}
                        {!isLink && (
                            <>
                                <div className="w-4 shrink-0" />
                                {BUTTON_SIZES.map((size) => (
                                    <div key={`icon-${size}`} className="w-14 shrink-0">
                                        {size} · icon
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                    {BUTTON_STATES.map((state) => (
                        <div
                            key={state}
                            className="flex items-center gap-spacing-lg border-t border-border-secondary py-spacing-md">
                            <div className="w-16 shrink-0 text-xs uppercase text-text-quaternary">
                                {STATE_LABEL[state]}
                            </div>
                            {BUTTON_SIZES.map((size) => (
                                <div key={size} className="w-44 shrink-0">
                                    <Button
                                        hierarchy={hierarchy}
                                        size={size}
                                        state={state}
                                        destructive={destructive}
                                        underlined={isLink}
                                        iconLeading={<DemoIcon />}
                                        iconTrailing={<DemoIcon />}>
                                        Button CTA
                                    </Button>
                                </div>
                            ))}
                            {!isLink && (
                                <>
                                    <div className="w-4 shrink-0" />
                                    {BUTTON_SIZES.map((size) => (
                                        <div key={`icon-${size}`} className="w-14 shrink-0">
                                            <Button
                                                hierarchy={hierarchy}
                                                size={size}
                                                state={state}
                                                destructive={destructive}
                                                iconOnly
                                                aria-label={`Call to action (${size})`}
                                                iconLeading={<DemoIcon />}
                                            />
                                        </div>
                                    ))}
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

export const ButtonShowcase: React.FC = () => (
    <Section
        title="Button (v2 component)"
        description={
            <>
                The first design-system <span className="font-semibold">component</span> generated
                from Figma by the component-sync pipeline. It composes the tokens above into a
                typed, token-only Tailwind component. Import v2 explicitly; the{' '}
                <InlineCode>@trustoo/components/Button</InlineCode> barrel still resolves to{' '}
                <InlineCode>v1</InlineCode> until v2 is promoted.
            </>
        }>
        <div className="space-y-spacing-3xl">
            <p className="text-xs text-text-tertiary">
                Laid out like the <span className="font-semibold">Buttons / Button</span> sheet in
                Figma: each block is one <span className="font-semibold">hierarchy</span>, columns
                are <span className="font-semibold">sizes</span> (left group = text with leading +
                trailing icon, right group = icon-only) and rows are the prop-driven{' '}
                <span className="font-semibold">states</span>. The{' '}
                <span className="font-semibold">Hover</span> and{' '}
                <span className="font-semibold">Focused</span> states exist in Figma too, but they
                are native <InlineCode>:hover</InlineCode> / <InlineCode>:focus-visible</InlineCode>{' '}
                styles on the real <InlineCode>&lt;button&gt;</InlineCode>, so hover or tab onto any
                button to see them live.
            </p>

            <div className="space-y-spacing-xl">
                {BUTTON_HIERARCHIES.map((hierarchy) => (
                    <ButtonMatrixBlock key={hierarchy} hierarchy={hierarchy} />
                ))}
            </div>

            <div className="space-y-spacing-xl">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">Destructive</h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        The <InlineCode>destructive</InlineCode> prop swaps the brand palette for
                        the <InlineCode>error-*</InlineCode> tokens, mirroring Figma's{' '}
                        <span className="font-semibold">Buttons / Button destructive</span> set. It
                        applies to every non-disabled hierarchy.
                    </p>
                </div>
                {DESTRUCTIVE_HIERARCHIES.map((hierarchy) => (
                    <ButtonMatrixBlock
                        key={`destructive-${hierarchy}`}
                        hierarchy={hierarchy}
                        destructive
                    />
                ))}
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={BUTTON_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>hierarchy</InlineCode> (primary · secondary · tertiary ·
                    link-color · link-gray), <InlineCode>size</InlineCode> (sm · md · lg · xl),{' '}
                    <InlineCode>state</InlineCode> (default · disabled · loading),{' '}
                    <InlineCode>destructive</InlineCode> (error palette),{' '}
                    <InlineCode>iconLeading</InlineCode> / <InlineCode>iconTrailing</InlineCode>,{' '}
                    <InlineCode>iconOnly</InlineCode> (requires <InlineCode>aria-label</InlineCode>
                    ), and <InlineCode>underlined</InlineCode> (link hierarchies only). All native{' '}
                    <InlineCode>&lt;button&gt;</InlineCode> props pass through, and the component
                    forwards its ref.
                </p>
            </div>
        </div>
    </Section>
)
